/* eslint-disable react-hooks/exhaustive-deps */
import { DogReproductionType, GenderType, PregnantPeriodType } from '@granlupo/api-client/generated/content/zeus'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import NextImage from 'next/legacy/image'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState, type FunctionComponent } from 'react'
import useLocalStorageState from 'use-local-storage-state'
import useWindowDimensions from '../../../hooks/useWindowDimensions'
import { createFetcher } from '../../../libs/next/api/jsonApiHandler'
import type { API } from '../../../pages/api/forms/[handler]'
import { filterNonEmpty } from '../../../utils/filterNonEmpty'
import { InternalLink } from '../../const/Links'
import { rephraseBreastfeeding, rephraseGender, rephrasePregnancyState, useRepraseAge } from '../../const/Rephrase'
import { BODY_CONDITION_SYSTEM_COEFICIENT, LARGE_SELECT_HEIGHT } from '../../const/digitContants'
import { Translation, useTranslate } from '../../contexts/TranslationContext'
import type { DogActivityResult } from '../../data/DogActivityFragment'
import style from '../../styles/Wizard.module.sass'
import SvgCross from '../../svg/icons/Cross'
import SvgTooltip from '../../svg/icons/Tooltip'
import { useActiveDog } from '../../user/ActiveDogContext'
import { useReviewFormDog } from '../../user/ReviewDogContext'
import { useAuth } from '../../user/UserContext'
import { useBestProductsTotalCount } from '../filter/ProductFilterControl'
import { Checkbox } from '../form/Checkbox'
import { ComboboxInput } from '../form/ComboboxInput'
import { ControlledComboBox } from '../form/ControlledComboBox'
import { NoOptionsBanner } from '../form/NoOptionsBanner'
import { RadioGroup } from '../form/RadioGroup'
import { UnitInput } from '../form/UnitInput'
import { Button } from '../shared/Button'
import { CustomTooltip } from '../shared/CustomTooltip'
import { Hidden } from '../shared/Hidden'
import { SuggestionModal } from '../shared/SuggestionModal'
import { DogInfoSchema, DogInfoSerializer, DogInfoStep1 } from './DogInfoSchema'
import { clearDogFormLocalStorage } from './clearDogFormLocalStorage'
import { useDogFormClear } from './useDogFormClear'

const api = createFetcher<API>('/api/forms/[handler]')

export const LS_DOG_INFO_KEY = 'dogInfo'

export function useLocalStorageDogInfo() {
	const [rawValue, setRawValue] = useLocalStorageState<string | undefined>(LS_DOG_INFO_KEY, {
		storageSync: true,
	})

	const value = useMemo(() => {
		if (rawValue) {
			return DogInfoSerializer.unserialize(rawValue)
		}
		return null
	}, [rawValue])

	const setValue = useCallback(
		(dogInfo: DogInfoSchema) => {
			setRawValue(DogInfoSerializer.serialize(dogInfo))
		},
		[setRawValue],
	)

	const clearValue = useCallback(() => {
		setRawValue(undefined)
	}, [setRawValue])

	return [value, setValue, clearValue] as const
}

const RadioInput = <ValueType,>(
	props: {
		title: React.ReactNode
		options:
			| {
					id: string
					label: string | undefined
					value?: string | number
					imageMain?: string
					imageFrom?: string
					imageTo?: string
			  }[]
			| null
			| undefined
		inRow?: boolean
		inGrid?: boolean
		mobileTooltip?: JSX.Element
		extraContent?: JSX.Element
		tooltipText?: JSX.Element
		isCentered?: boolean
		inGridSmall?: boolean
	} & (
		| {
				value: ValueType
				onChange: (value: ValueType) => void
				nullable?: false
		  }
		| {
				value: ValueType | null
				onChange: (value: ValueType | null) => void
				nullable: true
		  }
	),
) => {
	return (
		<RadioGroup
			title={props.title}
			inRow={props.inRow}
			extraContent={props.extraContent}
			inGrid={props.inGrid}
			inGridSmall={props.inGridSmall}
			value={String(props.value)}
			radioOptions={props.options ?? []}
			onChange={(e) => {
				props.onChange(e.currentTarget.value as unknown as ValueType)
			}}
			isCentered={props.isCentered}
			tooltipText={props.tooltipText}
			mobileTooltip={props.mobileTooltip}
			isNullAllowed={props.nullable}
			nullClick={
				props.nullable
					? () => {
							props.onChange(null)
						}
					: undefined
			}
		/>
	)
}

const CheckboxListInput = <ValueType extends string>(props: {
	value: ValueType[]
	clear: () => void
	onChange: (value: ValueType[]) => void
	options: { id: ValueType; label: string | undefined }[] | null | undefined
}) => {
	const translate = useTranslate()

	return (
		<div className={style.checkboxList}>
			{props.options?.map((option) => (
				<Checkbox
					key={option.id}
					value={option.id}
					label={option.label ?? option.id}
					checked={props.value.includes(option.id)}
					onChange={() =>
						props.onChange(
							props.value.includes(option.id)
								? props.value.filter((v) => v !== option.id)
								: [...props.value, option.id],
						)
					}
				/>
			))}
			{props.value.length > 0 && (
				<div className={style.buttonWrapper}>
					<Button
						label={translate('checkboxGroup.clear')}
						icon={{ small: true }}
						iconBefore={<SvgCross />}
						type={{ variant: 'underlinedGrey' }}
						onClick={props.clear}
					/>
				</div>
			)}
		</div>
	)
}

export function useBreedOptions() {
	return useQuery({
		queryKey: ['breedOptions'],
		queryFn: async () => {
			return api('breedOptions', {})
		},
	})
}

export function useAgesOptions(breed: string | null) {
	const reprahseAge = useRepraseAge()
	return useQuery({
		queryKey: ['ageOptions', breed],
		queryFn: async () => {
			return breed
				? api('ageOptions', { _req: { query: { breed } } }).then((data) =>
						data
							.map((age) => {
								const label = reprahseAge(age)
								return label
									? {
											id: age.id,
											label,
											type: age.categoryLabel,
										}
									: undefined
							})
							.filter(filterNonEmpty),
					)
				: null
		},
		enabled: Boolean(breed),
	})
}

export const useDogFormData = (gender: GenderType | null, breed: string | null) => {
	const products = useQuery({
		queryKey: ['productOptions'],
		queryFn: async () => {
			return api('productOptions', {})
		},
	})

	const breeds = useBreedOptions()

	const ages = useAgesOptions(breed)

	const activities = useQuery({
		queryKey: ['activityOptions'],
		queryFn: async () => {
			return api('activityOptions', {})
		},
	})

	const bodyTypes = useQuery({
		queryKey: ['bodyTypeOptions'],
		queryFn: async () => {
			return api('bodyTypeOptions', {})
		},
	})

	const puppyIdealWeightInAdulthood = useQuery({
		queryKey: ['bodyTypeOptions', gender, breed],
		queryFn: async () => {
			return gender && breed
				? api('idealWeight', {
						_req: { query: { gender: gender, breed: breed } },
					})
				: null
		},
	})

	const breastfeedingOptions = useQuery({
		queryKey: ['breastfeedingOptions'],
		queryFn: async () => {
			return api('breastfeedingOptions', {}).then((data) =>
				data.map((item) => ({ id: item.id, label: rephraseBreastfeeding(item.week) })),
			)
		},
		enabled: gender === GenderType.female,
	})

	const illnessesOptions = useQuery({
		queryKey: ['illnessOptions'],
		queryFn: async () => {
			return api('illnessOptions', {})
		},
	})

	const allergiesOptions = useQuery({
		queryKey: ['allergyOptions'],
		queryFn: async () => {
			return api('allergyOptions', {})
		},
	})

	return {
		products,
		breeds,
		ages,
		activities,
		bodyTypes,
		breastfeedingOptions,
		puppyIdealWeightInAdulthood,
		illnessesOptions,
		allergiesOptions,
	}
}

const PregnantPeriodTypeInput: FunctionComponent<{
	value: PregnantPeriodType | null
	onChange: (value: PregnantPeriodType | null) => void
}> = (props) => {
	const translate = useTranslate()
	return (
		<RadioInput
			inRow
			title={translate('wizard.secondScreen.health.checkbox.pregnant')}
			value={props.value}
			onChange={props.onChange}
			options={[
				{
					id: PregnantPeriodType.firstHalf as string,
					label: rephrasePregnancyState(PregnantPeriodType.firstHalf),
				},
				{
					id: PregnantPeriodType.secondHalf as string,
					label: rephrasePregnancyState(PregnantPeriodType.secondHalf),
				},
			]}
			nullable
		/>
	)
}

export const DogInfoForm: FunctionComponent<{
	dogActivities?: DogActivityResult[]
	dogBodyTypes: { id: string; value: number }[]
}> = (props) => {
	const router = useRouter()
	const auth = useAuth()
	const { dog, saveActiveDog, isLoading } = useActiveDog()

	const queryClient = useQueryClient()
	const handleSuccess = useCallback(
		async (value: DogInfoSchema) => {
			saveActiveDog(value)
			await queryClient.resetQueries()
			await router.push(InternalLink.MyResults)
		},
		[router, saveActiveDog],
	)

	const handleChange = useCallback((change: NullableDogInfoSchema) => {
		console.log({ change })
	}, [])

	const [firstLoad, setFirstLoad] = useState(true)

	const [requestClear, setRequestClear] = useDogFormClear()

	if (requestClear && firstLoad) {
		clearDogFormLocalStorage()
		setRequestClear(false)
	}
	if (firstLoad) {
		setFirstLoad(false)
	}

	if (isLoading || requestClear) {
		return null
	}

	return (
		<DogInfoFormInner
			key={[auth.status, dog?.id].join(',')}
			value={dog}
			dogBodyTypes={props.dogBodyTypes}
			dogActivities={props.dogActivities}
			onSuccess={handleSuccess}
			onChange={handleChange}
		/>
	)
}

type NullableValues<R extends Record<string, unknown>> = {
	[K in keyof R]: R[K] | null
}

type NullableDogInfoSchema = NullableValues<DogInfoSchema>

export const DogInfoFormInner: FunctionComponent<{
	dogActivities?: DogActivityResult[]
	dogBodyTypes: { id: string; value: number }[]
	value: DogInfoSchema | null
	onChange?: (value: NullableDogInfoSchema) => void
	onSuccess?: (value: DogInfoSchema) => void
}> = (props) => {
	const id = props.value?.id
	const router = useRouter()
	const { query } = useRouter()

	const routerBasedGender = query.gender === 'female' ? GenderType.female : GenderType.male
	const mediumBodyType = props.dogBodyTypes.find((bodyType) => bodyType.value === 5)

	const firstActivityId = props.dogActivities && props.dogActivities.length > 0 ? props.dogActivities[0].id : undefined

	const [gender, setGender] = useLocalStorageState('dogFormGender', {
		defaultValue: props.value?.gender ?? routerBasedGender ?? GenderType.male,
	})
	const [breed, setBreed] = useLocalStorageState('dogFormBreed', {
		defaultValue: props.value?.breed ?? null,
	})

	const [weight, setWeight] = useLocalStorageState('dogFormWeight', {
		defaultValue: props.value?.weight ?? null,
	})
	const [breastfeedingPuppyCount, setBreastfeedingPuppyCount] = useLocalStorageState('dogFormBreastfeedingPuppyCount', {
		defaultValue:
			props.value?.reproduction?.type === DogReproductionType.breastfeeding
				? props.value?.reproduction.breastfeedingPuppyCount
				: null,
	})

	const {
		products,
		ages,
		activities,
		bodyTypes,
		breeds,
		breastfeedingOptions,
		illnessesOptions,
		allergiesOptions,
		puppyIdealWeightInAdulthood,
	} = useDogFormData(gender, breed)

	const [age, setAge] = useLocalStorageState('dogFormAge', {
		defaultValue: props.value?.age ?? null,
	})

	const [bodyType, setBodyType] = useLocalStorageState('dogFormBodyType', {
		defaultValue: props.value?.bodyType ?? mediumBodyType?.id ?? null,
	})
	const [fedProduct, setFedProduct] = useLocalStorageState('dogFormFedProduct', {
		defaultValue: props.value?.fedProduct ? props.value.fedProduct : props.value ? 'undisclosed' : null,
	})
	const [activity, setActivity] = useLocalStorageState('dogFormActivity', {
		defaultValue: props.value?.activity ?? firstActivityId ?? null,
	})

	const [reproduction, setReproduction] = useLocalStorageState('dogFormReproduction', {
		defaultValue: props.value?.reproduction ?? null,
	})
	const [illnesses, setIllnesses] = useLocalStorageState('dogFormIllnesses', {
		defaultValue: props.value?.illnesses ?? null,
	})
	const [allergies, setAllergies] = useLocalStorageState('dogFormAllergies', {
		defaultValue: props.value?.allergies ?? null,
	})

	const toggleReproduction = useCallback((value: null | DogInfoSchema['reproduction']) => {
		return setReproduction((old) => {
			return old?.type === value?.type ? null : value
		})
	}, [])

	const ageItem = ages.data?.find((item: { id: string }) => item.id === age)
	const isJunior = String(ageItem?.type).startsWith('puppy')

	const getIdealWeight = useCallback(
		(weight: number | null, bodyType: string | null) => {
			if (isJunior) {
				return Number(puppyIdealWeightInAdulthood.data?.averageWeight)
			}
			if (weight && weight !== 0 && bodyType) {
				const bodyTypeValue = props.dogBodyTypes?.find((type) => type.id === bodyType)
				const idealWeight =
					(weight * 100) / (100 + (Number(bodyTypeValue?.value) - BODY_CONDITION_SYSTEM_COEFICIENT) * 10)
				return Math.floor(idealWeight * 10) / 10
			}
			return Number(puppyIdealWeightInAdulthood.data?.averageWeight)
		},
		[isJunior, props.dogBodyTypes, puppyIdealWeightInAdulthood],
	)

	const idealWeightCount = getIdealWeight(weight, bodyType)
	const [idealWeight, setIdealWeight] = useLocalStorageState('dogFormIdealWeight', {
		defaultValue: props.value?.idealWeight ?? null,
	})

	useEffect(() => {
		if (reproduction?.type === DogReproductionType.breastfeeding) {
			setReproduction(() => ({
				type: DogReproductionType.breastfeeding,
				breastfeedingCategory: { id: reproduction?.breastfeedingCategory.id },
				breastfeedingPuppyCount: breastfeedingPuppyCount ?? 1,
			}))
		}
	}, [breastfeedingPuppyCount])

	const translate = useTranslate()

	const schema = useMemo<NullableDogInfoSchema>(() => {
		return {
			gender,
			fedProduct: fedProduct ?? 'undisclosed',
			breed,
			age,
			activity,
			bodyType,
			weight,
			idealWeight: idealWeight ?? idealWeightCount,
			reproduction,
			illnesses,
			allergies,
		}
	}, [activity, age, allergies, bodyType, breed, fedProduct, gender, idealWeight, illnesses, reproduction, weight])

	const productCountSchema = useMemo<DogInfoSchema | undefined>(() => {
		if (breed && age && bodyType && weight) {
			return {
				gender,
				fedProduct,
				breed,
				age,
				activity,
				bodyType,
				weight,
				idealWeight,
				reproduction,
				illnesses,
				allergies,
			}
		}
		return undefined
	}, [activity, age, allergies, bodyType, breed, fedProduct, gender, idealWeight, illnesses, reproduction, weight])

	const onChange = props.onChange

	const [isModalOpen, setIsModalOpen] = useState(false)

	useEffect(() => {
		console.log('onChange', schema)
		onChange?.(schema)
	}, [schema, onChange])

	const step1 = useMemo(() => {
		return DogInfoStep1.safeParse(schema)
	}, [schema])

	const dogInfo = useMemo(() => {
		return DogInfoSchema.safeParse({ id, ...schema })
	}, [id, schema])

	const toggleHealth = useCallback(() => {
		if (illnesses || allergies) {
			setIllnesses(null)
			setAllergies(null)
			return
		} else {
			setIllnesses((old) => (Array.isArray(old) ? null : []))
			setAllergies((old) => (Array.isArray(old) ? null : []))
		}
	}, [])

	const removeAll = useCallback(() => {
		setIllnesses(null)
		setAllergies(null)
		setBodyType(mediumBodyType?.id ?? null)
		setAge(null)
		setGender(GenderType.male)
		setWeight(null)
		setIdealWeight(null)
		setReproduction(null)
		setFedProduct(null)
		setBreed(null)
		setActivity(firstActivityId)
	}, [])

	const { width } = useWindowDimensions()
	const isMobile = Number(width) <= 768

	const [, setRequestClear] = useDogFormClear()

	const results = useBestProductsTotalCount({ dogInfo: productCountSchema })

	const resultsCount =
		results.isSuccess && typeof results.data?.totalCount === 'number' && weight
			? ` ${translate('wizard.secondScreen.button.results.sum')} (${results.data.totalCount})`
			: ''

	const updateStep = (value: 1 | 2) => {
		router.push({ pathname: router.pathname, query: { ...query, step: value } }, undefined, {
			scroll: false,
		})
	}
	const [isReseted, setIsReseted] = useState(false)
	const [isActivated, setIsActivated] = useState(false)

	const { removeActiveDog } = useReviewFormDog()

	const isDisabled = typeof idealWeight !== 'number' || (idealWeight === idealWeightCount && !isActivated)
	const juniorIdealWeight = isDisabled ? idealWeightCount : idealWeight
	const illogicWeightData = isJunior && Number(weight) > juniorIdealWeight

	return (
		<div className={style.wrapper}>
			<SuggestionModal isModalOpen={isModalOpen} handleClose={() => setIsModalOpen(false)} />
			{(!query.step || query.step === '1') && (
				<>
					<h1 className={style.title}>
						<Translation tr="wizard.firstScreen.title" />
					</h1>
					<ControlledComboBox
						key={String('product' + products.isSuccess + fedProduct)}
						label={
							<h2 className={style.inputTitle}>
								<Translation tr="wizard.firstScreen.fedProduct.label" />
							</h2>
						}
						state={
							[
								fedProduct,
								products.data
									? (newFedProduct) => {
											if (newFedProduct === '') {
												setFedProduct(null)
											} else {
												setFedProduct(newFedProduct)
											}
										}
									: () => undefined,
							] as const
						}
						items={products.data ?? []}
						height={LARGE_SELECT_HEIGHT}
						isResetedState={[isReseted, setIsReseted]}
						hasPreview
						hasMinLength
						hasEmptyOption
						isArrowHidden
						placeholder={translate('dogInfoForm.fedProduct.placeholder')}
						noOptionsMessage={
							<NoOptionsBanner
								openModal={() => setIsModalOpen(true)}
								title={translate('noOptionBanner.title.product')}
							/>
						}
					/>
					<ControlledComboBox
						key={String('breed' + breeds.isSuccess + breed)}
						label={
							<h2 className={style.inputTitle}>
								<Translation tr="wizard.firstScreen.breed.label" />
							</h2>
						}
						isResetedState={[isReseted, setIsReseted]}
						state={
							[
								breed,
								breeds.data
									? (newBreed) => {
											if (newBreed !== breed || !ageItem) {
												setAge(null)
												setWeight(0)
												setIdealWeight(null)
												setReproduction(null)
												setBodyType(mediumBodyType?.id ?? null)
											}
											setBreed(newBreed)
										}
									: () => undefined,
							] as const
						}
						items={breeds.data ?? []}
						height={LARGE_SELECT_HEIGHT}
						placeholder={translate('dogInfoForm.breed.placeholder')}
						noOptionsMessage={
							<NoOptionsBanner openModal={() => setIsModalOpen(true)} title={translate('noOptionBanner.title.breed')} />
						}
					/>
					<h2 className={style.inputTitle}>
						<label>
							<Translation tr="wizard.firstScreen.age.label" />
						</label>
					</h2>
					<div className={style.selectGrid}>
						<ComboboxInput
							name={translate('wizard.firstScreen.age.label')}
							state={
								[
									age,
									ages.data
										? (newAge) => {
												if (newAge !== age) {
													setReproduction(null)
													setWeight(0)
													setIdealWeight(null)
													setBodyType(mediumBodyType?.id ?? null)
												}
												setAge(newAge)
											}
										: () => undefined,
								] as const
							}
							searchPlaceholder={translate('dogInfoForm.age.placeholder')}
							items={ages.data ?? []}
							isNotSearchable
							noOptionsMessage={translate('dogInfoForm.breed.first.noOptions')}
							height={LARGE_SELECT_HEIGHT}
						/>
					</div>
					<div className={style.selectGridMultiple}>
						<RadioInput
							title={translate('dogInfoForm.gender.label')}
							value={gender}
							onChange={(value) => {
								setGender(value)
								setReproduction(null)
							}}
							options={[
								{
									id: GenderType.male as string,
									label: rephraseGender(GenderType.male),
								},
								{
									id: GenderType.female as string,
									label: rephraseGender(GenderType.female),
								},
							]}
							inGrid
							inRow
						/>
					</div>

					<RadioInput<string | null>
						title={<span className={style.activityTooltip}>{translate('wizard.firstScreen.activity.title')}</span>}
						extraContent={
							<>
								<span
									className={style.activityTooltipIcon}
									data-tooltip-id={isMobile ? 'tooltipAgeMobile' : 'tooltipAge'}
									data-tip
								>
									<SvgTooltip />
								</span>
							</>
						}
						value={activity}
						onChange={setActivity}
						options={activities.data}
						inRow
						inGrid
					/>

					<CustomTooltip
						tooltipId={isMobile ? 'tooltipAgeMobile' : 'tooltipAge'}
						textAfterHover={
							<div className={style.activityTooltipTable}>
								<table>
									<tbody>
										<tr className={style.activityTooltipTableHeader}>
											<th className={style.activityTooltipTableHeaderItem}>
												{translate('wizard.activityTooltip.table.header.dailyLoad')}
											</th>
											<th className={style.activityTooltipTableHeaderItem}>
												{translate('wizard.activityTooltip.table.header.example')}
											</th>
										</tr>
										{props.dogActivities?.map((activity) => (
											<tr key={activity.id} className={style.activityTooltipRow}>
												<td className={style.activityTooltipTableLabel}>{activity.label}</td>
												<td className={style.activityTooltipTableExample}>
													{translate(`wizard.activityTooltip.table.cell.${[activity.remoteId]}`)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						}
					/>
				</>
			)}
			{query.step === '2' && (
				<>
					<h1 className={style.title}>
						{isJunior ? (
							<Translation tr="wizard.secondScreen.title.puppy" />
						) : gender === GenderType.female ? (
							<Translation tr="wizard.secondScreen.title.adult.female" />
						) : (
							<Translation tr="wizard.secondScreen.title.adult.male" />
						)}
					</h1>
					{!isJunior && (
						<>
							<h2 className={style.inputTitle}>
								<Translation tr="wizard.bodyType.title" />
							</h2>
							<div className={style.categoriesWrapper}>
								{bodyTypes.data?.map((category) => (
									<div key={category.id}>
										<Hidden breakpoint="mobileDown">
											<div className={style.imageWrapper}>
												{category.imageMain && (
													<NextImage
														src={category.imageMain.url}
														width={category.imageMain.width}
														height={category.imageMain.height}
														title={category.imageMain.alt}
														alt={category.imageMain.alt ?? ''}
														layout="intrinsic"
													/>
												)}
											</div>
										</Hidden>
										<RadioInput
											title={category.name}
											value={bodyType}
											onChange={setBodyType}
											options={category.items.map((item) => ({
												id: item.id,
												label: item.name,
												description: item.lead,
												value: item.value,
												imageMain: item.imageMain?.url,
												imageFrom: item.imageFrom?.url,
												imageTo: item.imageTo?.url,
											}))}
											isCentered
										/>
									</div>
								))}
							</div>
						</>
					)}
					<div className={clsx(style.unitWrapper, isJunior && style.unitWrapperNoneBefore)}>
						<div className={style.unitGroup}>
							<div className={style.unitItem}>
								<UnitInput
									label={translate('wizard.secondScreen.weight.label')}
									tooltip={translate('wizard.secondScreen.weight.tooltip.title')}
									tooltipText={translate('wizard.secondScreen.weight.tooltip.text')}
									unit={translate('wizard.secondScreen.weight.unit')}
									name="weight"
									value={weight !== null && weight > 0 ? weight : undefined}
									placeholder="0"
									onChange={(e) => {
										setWeight(e.currentTarget.valueAsNumber)
									}}
								/>
							</div>
							<div className={style.unitItem}>
								<UnitInput
									label={
										isJunior
											? translate('wizard.secondScreen.idealWeight.label.junior')
											: translate('wizard.secondScreen.idealWeight.label.adult')
									}
									tooltip={translate('wizard.secondScreen.idealWeight.tooltip.title')}
									tooltipText={translate('wizard.secondScreen.idealWeight.tooltip.text')}
									unit={translate('wizard.secondScreen.idealWeight.unit')}
									name="customIdealWeight"
									value={typeof idealWeight === 'number' ? idealWeight : idealWeightCount}
									onChange={(e) => setIdealWeight(e.currentTarget.valueAsNumber)}
									disabled={Boolean(isDisabled)}
									placeholder={idealWeight ? String(idealWeight) : '0'}
									buttonContent={
										<div className={clsx(style.unitButtonWrapper)}>
											<Button
												type={{ variant: 'underlined' }}
												onClick={() => {
													if (isDisabled) {
														setIdealWeight(idealWeightCount)
														setIsActivated(true)
													} else {
														setIdealWeight(null)
														setIsActivated(false)
													}
												}}
												label={translate(
													isDisabled
														? 'wizard.secondScreen.idealWeight.adjust'
														: 'wizard.secondScreen.idealWeight.reset',
												)}
											/>
										</div>
									}
								/>
							</div>
						</div>
					</div>
					<h2 className={style.inputTitle}>
						<Translation tr="wizard.secondScreen.health.title" />
					</h2>
					<div className={style.checkboxGroupWrapper}>
						<Checkbox
							checked={!Array.isArray(illnesses) && !Array.isArray(allergies)}
							label={
								gender === GenderType.female
									? translate('wizard.secondScreen.health.checkbox.healthy.female')
									: translate('wizard.secondScreen.health.checkbox.healthy.male')
							}
							onChange={toggleHealth}
						/>
						{!isJunior && (
							<Checkbox
								checked={reproduction?.type === DogReproductionType.castrated}
								label={
									gender === GenderType.female
										? translate('wizard.secondScreen.health.checkbox.castrated.female')
										: translate('wizard.secondScreen.health.checkbox.castrated.male')
								}
								onChange={() => toggleReproduction({ type: DogReproductionType.castrated })}
							/>
						)}
						<Checkbox
							checked={Array.isArray(allergies)}
							label={translate('wizard.secondScreen.health.checkbox.allergies')}
							onChange={() => setAllergies((old) => (Array.isArray(old) ? null : []))}
						/>
						<Checkbox
							checked={Array.isArray(illnesses)}
							label={translate('wizard.secondScreen.health.checkbox.illnesses')}
							onChange={() => setIllnesses((old) => (Array.isArray(old) ? null : []))}
						/>

						{gender === GenderType.female && !isJunior && (
							<Checkbox
								checked={reproduction?.type === DogReproductionType.pregnant}
								label={translate('wizard.secondScreen.health.checkbox.pregnant')}
								onChange={() =>
									toggleReproduction({
										type: DogReproductionType.pregnant,
										pregnancy: PregnantPeriodType.firstHalf,
									})
								}
							/>
						)}
						{gender === GenderType.female && !isJunior && (
							<Checkbox
								checked={reproduction?.type === DogReproductionType.breastfeeding}
								label={translate('wizard.secondScreen.health.checkbox.breastfeeding')}
								onChange={() => {
									const item = breastfeedingOptions.data?.[0]
									if (item) {
										toggleReproduction({
											type: DogReproductionType.breastfeeding,
											breastfeedingCategory: { id: item.id },
											breastfeedingPuppyCount:
												reproduction?.type === DogReproductionType.breastfeeding
													? reproduction?.breastfeedingPuppyCount
													: 1,
										})
									}
								}}
							/>
						)}
					</div>

					{reproduction?.type === DogReproductionType.pregnant && (
						<div>
							<PregnantPeriodTypeInput
								value={reproduction.pregnancy}
								onChange={(pregnancy) =>
									pregnancy ? setReproduction({ type: DogReproductionType.pregnant, pregnancy }) : setReproduction(null)
								}
							/>
						</div>
					)}
					{reproduction?.type === DogReproductionType.breastfeeding && (
						<div>
							<RadioInput
								inRow
								title={translate('wizard.secondScreen.health.checkbox.breastfeeding')}
								options={breastfeedingOptions.data}
								value={reproduction.breastfeedingCategory.id}
								onChange={(breastfeedingCategory) =>
									breastfeedingCategory
										? setReproduction((old) => ({
												type: DogReproductionType.breastfeeding,
												breastfeedingCategory: { id: breastfeedingCategory },
												breastfeedingPuppyCount:
													(old?.type === DogReproductionType.breastfeeding ? old.breastfeedingPuppyCount : null) ?? 1,
											}))
										: setReproduction(null)
								}
								nullable
							/>
							<UnitInput
								label={translate('wizard.secondScreen.breastfeeding.count')}
								name="breastfeedingPuppyCount"
								value={reproduction.breastfeedingPuppyCount ?? undefined}
								placeholder="1"
								min={1}
								onChange={(e) => setBreastfeedingPuppyCount(e.currentTarget.valueAsNumber)}
							/>
						</div>
					)}
					{Array.isArray(allergies) && (
						<div>
							<h2 className={style.inputTitle}>
								<Translation tr="wizard.secondScreen.health.checkbox.allergies" />
							</h2>
							<div className={style.checkboxListWrapper}>
								<CheckboxListInput
									value={allergies}
									onChange={setAllergies}
									options={allergiesOptions.data}
									clear={() => setAllergies(null)}
								/>
							</div>
						</div>
					)}
					{Array.isArray(illnesses) && (
						<div>
							<h2 className={style.inputTitle}>
								<Translation tr="wizard.secondScreen.health.checkbox.illnesses" />
							</h2>
							<div className={style.checkboxListWrapper}>
								<CheckboxListInput
									value={illnesses}
									onChange={setIllnesses}
									options={illnessesOptions.data}
									clear={() => setIllnesses(null)}
								/>
							</div>
						</div>
					)}
				</>
			)}
			<div className={style.footWrapper}>
				<div className={style.foot}>
					{query.step === '2' && (
						<div className={clsx(style.footButton, style.backButtonWrapper)}>
							<Button
								type={{ variant: 'naked' }}
								label={translate('wizard.secondScreen.button.back')}
								onClick={() => {
									updateStep(1)
									window.scrollTo(0, 0)
								}}
							/>
						</div>
					)}

					<div className={clsx(style.footButton, style.buttonWrapper)}>
						{query.step === '2' ? (
							<Button
								disabled={!dogInfo.success || illogicWeightData || results.data?.totalCount === 0}
								label={`${translate('wizard.secondScreen.button.results')}${resultsCount}`}
								onClick={() => {
									if (dogInfo.success) {
										const validAge = ages.data?.find((item) => item.id === dogInfo.data.age)
										if (validAge) {
											removeActiveDog()
											props.onSuccess?.(dogInfo.data)
											setRequestClear(true)
										} else {
											alert(translate('wizard.alert.age.breed'))
										}
									} else {
										alert(translate('wizard.alert.something.wrong'))
									}
								}}
							/>
						) : (
							<Button
								disabled={!step1.success}
								label={translate('wizard.secondScreen.button.next')}
								onClick={() => {
									updateStep(2)
									window.scrollTo(0, 0)
									if (props.value?.gender !== gender) {
										setReproduction(null)
									}
								}}
							/>
						)}
					</div>
				</div>

				{illogicWeightData && query.step === '2' && (
					<div className={style.validateDataMessage}>
						<Translation tr="wizard.secondScreen.validation.illogical.data" />
						<div className={style.validateDataMessageList}>
							{[...Array(4)].map((_elem, index) => (
								<span className={style.validateDataMessageItem} key={index}>
									{index + 1}. <Translation tr={`wizard.secondScreen.validation.illogical.${index + 1}`} />
									{index < 3 ? ',' : ''}
								</span>
							))}
						</div>
					</div>
				)}
				{(fedProduct || breed || age || gender !== 'male' || activity !== firstActivityId) && (
					<div className={style.buttonCrossReset}>
						<Button
							iconBefore={
								<div className={style.buttonCrossResetIcon}>
									<SvgCross />
								</div>
							}
							icon={{ small: true }}
							label={translate('wizard.removeAll')}
							type={{ variant: 'naked' }}
							onClick={() => {
								if (query.step === '2') {
									removeAll()
									setIsReseted(true)
									updateStep(1)
									window.scrollTo(0, 0)
								}
								removeAll()
								setIsReseted(true)
							}}
						/>
					</div>
				)}
			</div>
		</div>
	)
}
